/**
 * Basic cookie utility functions
 */

import type { CookieOptions, DeleteCookieOptions } from './types'

/**
 * Converts all cookies into a key-value object
 * @returns Object containing all cookies as key-value pairs
 */
export const cookieAsObject = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {}
  }

  return window?.document?.cookie
    .split(';')
    .map(x => x.trim())
    .map(x => {
      // Use only the first '=' as the split point
      const splitOn = x.indexOf('=')
      return [x.slice(0, splitOn), x.slice(splitOn + 1)]
    })
    .reduce(
      (acc, [k, v]) => {
        acc[k] = v
        return acc
      },
      {} as Record<string, string>,
    )
}

/**
 * Gets a specific cookie by name
 * @param name - The name of the cookie to retrieve
 * @returns The cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
  return cookieAsObject()[name]
}

/**
 * Sets a cookie with the given name, value, and optional parameters
 * @param name - The name of the cookie
 * @param value - The value of the cookie
 * @param options - Optional cookie parameters (expires, path, domain, secure, sameSite)
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  if (typeof window === 'undefined') {
    return
  }

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

  if (options.expires) {
    const expires =
      options.expires instanceof Date
        ? options.expires
        : new Date(options.expires)
    cookieString += `; expires=${expires.toUTCString()}`
  }

  if (options.path) {
    cookieString += `; path=${options.path}`
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`
  }

  if (options.secure) {
    cookieString += '; secure'
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`
  }

  document.cookie = cookieString
}

/**
 * Deletes a cookie by setting its expiration date to the past
 * @param name - The name of the cookie to delete
 * @param options - Optional cookie parameters (path, domain)
 */
export const deleteCookie = (
  name: string,
  options: DeleteCookieOptions = {},
): void => {
  setCookie(name, '', {
    ...options,
    expires: new Date(0),
  })
}
